import { useState, useEffect } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { PaymentProcessor, PaymentProcessorUsageFunction } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  processor: PaymentProcessor | null;
  onSave: (processorId: string, functions: PaymentProcessorUsageFunction[]) => void;
}

const USAGE_ICONS: Record<string, string> = {
  new_radio: 'ri-add-circle-line',
  renewal: 'ri-loop-left-line',
  annual_license: 'ri-calendar-check-line',
  preferred_frequency: 'ri-signal-tower-line',
  premium_domain: 'ri-global-line',
  plan_upgrade: 'ri-arrow-up-circle-line',
  marketplace: 'ri-store-2-line',
};

const USAGE_DESCRIPTIONS: Record<string, string> = {
  new_radio: 'Cobro único por activación de radio nueva',
  renewal: 'Cobro recurrente por renovación del plan',
  annual_license: 'Cobro anual por licencia de operación',
  preferred_frequency: 'Cobro único por reserva de frecuencia preferida',
  premium_domain: 'Cobro por dominio .radio o personalizado',
  plan_upgrade: 'Cobro por diferencia al subir de plan',
  marketplace: 'Cobro por comisiones en marketplace (próximamente)',
};

export default function ProcessorUsageModal({ open, onClose, processor, onSave }: Props) {
  const [functions, setFunctions] = useState<PaymentProcessorUsageFunction[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open && processor?.usageFunctions) {
      setFunctions(processor.usageFunctions.map((f) => ({ ...f })));
      setDirty(false);
    }
  }, [open, processor]);

  if (!processor) return null;

  const handleToggle = (fnId: string) => {
    setDirty(true);
    setFunctions((prev) =>
      prev.map((f) => (f.id === fnId ? { ...f, enabled: !f.enabled } : f)),
    );
  };

  const enabledCount = functions.filter((f) => f.enabled).length;

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 600));
    onSave(processor.id, functions);
    setSaving(false);
    setDirty(false);
    SwalAlert.toast(`Usos de ${processor.name} actualizados`, 'success');
    onClose();
  };

  const handleCancel = () => {
    if (dirty) {
      SwalAlert.confirm(
        '¿Descartar cambios?',
        'Los cambios realizados en los usos no se guardarán.',
      ).then((r) => {
        if (r.isConfirmed) {
          setDirty(false);
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  return (
    <ModalBase
      open={open}
      onClose={handleCancel}
      title={`Dónde se usa ${processor.name}`}
      subtitle={`${enabledCount} de ${functions.length} funciones activas`}
      size="md"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${processor.logoColor}`}>
          <i className={`${processor.icon} text-sm`}></i>
        </div>
      }
      footer={
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={handleCancel}
            className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-wait flex items-center gap-1.5"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xs"></i>Guardando...
              </>
            ) : (
              'Guardar cambios'
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Info banner */}
        <div className="bg-accent-100/60 border border-accent-200/60 rounded-xl p-3 flex items-start gap-2.5">
          <div className="w-5 h-5 rounded-full bg-accent-200 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-accent-600 text-[10px]"></i>
          </div>
          <p className="text-foreground-700 text-[10px] leading-relaxed">
            Definí para qué tipos de cobro se puede usar <strong>{processor.name}</strong>. Si desmarcás una función, ese tipo de cobro no aceptará este procesador aunque esté activo.
          </p>
        </div>

        {/* Usage toggles */}
        <div className="bg-background-100 rounded-xl overflow-hidden divide-y divide-background-200/70">
          {functions.map((fn) => (
            <div
              key={fn.id}
              className="flex items-center justify-between gap-3 p-3 hover:bg-background-50 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${fn.enabled ? 'bg-emerald-50' : 'bg-background-200'}`}>
                  <i className={`${USAGE_ICONS[fn.id] || 'ri-checkbox-blank-line'} text-sm ${fn.enabled ? 'text-emerald-500' : 'text-foreground-300'}`}></i>
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-semibold truncate ${fn.enabled ? 'text-foreground-800' : 'text-foreground-400'}`}>
                    {fn.label}
                  </p>
                  <p className="text-foreground-400 text-[9px] truncate mt-0.5">
                    {USAGE_DESCRIPTIONS[fn.id] || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleToggle(fn.id)}
                className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${fn.enabled ? 'bg-emerald-500' : 'bg-background-300'}`}
              >
                <span
                  className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${fn.enabled ? 'translate-x-4.5' : 'translate-x-0.5'}`}
                ></span>
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Funciones activas</p>
            <p className="text-foreground-800 text-sm font-bold mt-0.5">
              {enabledCount} <span className="text-foreground-300 font-normal text-[10px]">/ {functions.length}</span>
            </p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium">Procesador</p>
            <p className={`text-sm font-bold mt-0.5 ${processor.enabled ? 'text-emerald-600' : 'text-foreground-400'}`}>
              {processor.enabled ? 'Activo' : 'Inactivo'}
            </p>
          </div>
        </div>
      </div>

      <ModalBase
        open={false}
        onClose={() => {}}
        title=""
        footer={<></>}
      />
    </ModalBase>
  );
}